import { buildiNatUrls } from './getiNatData'
import { SANITY_URL } from './sanityApi'
import { API_CACHE, IMAGE_CACHE } from './cacheNames'

// Download every API response and card image for a location into the
// Cache Storage caches the service worker serves from, so the whole
// field guide is browsable offline. Writes directly with cache.put so it
// works even on the very first visit (before the service worker controls
// the page). Reports progress via onProgress({ done, total }).

const fetchAndCache = async (cache, url, mode) => {
    const res = await fetch(url, mode ? { mode } : undefined)
    if (res && (res.ok || res.type === 'opaque')) {
        await cache.put(url, res.clone())
    }
    return res
}

// Run tasks with limited concurrency so we don't fire thousands of
// simultaneous image requests.
const runBatched = async (tasks, batchSize, onEach) => {
    for (let i = 0; i < tasks.length; i += batchSize) {
        await Promise.all(
            tasks.slice(i, i + batchSize).map(t =>
                t().catch(e => console.log('offline sync: skipped', e)).then(onEach)
            )
        )
    }
}

const syncOffline = async (location, onProgress) => {
    const apiCache = await caches.open(API_CACHE)
    const imageCache = await caches.open(IMAGE_CACHE)

    const iNatUrls = buildiNatUrls(location.bbox, false, location.project_id)
    const firstUrls = buildiNatUrls(location.bbox, true, location.project_id)
    const apiUrls = [SANITY_URL, ...firstUrls.map(o => o.url), ...iNatUrls.map(o => o.url)]

    // Phase 1: cache all API responses, collecting species for the image list.
    const speciesResults = []
    let done = 0
    let total = apiUrls.length
    onProgress && onProgress({ done, total })

    const apiTasks = apiUrls.map(url => async () => {
        const res = await fetchAndCache(apiCache, url, 'cors')
        if (url !== SANITY_URL && res && res.ok) {
            const data = await res.json()
            speciesResults.push(...(data.results || []))
        }
    })
    await runBatched(apiTasks, 6, () => {
        done += 1
        onProgress && onProgress({ done, total })
    })

    // Phase 2: cache card thumbnails and detail images for every species.
    const imageUrls = new Set()
    for (const s of speciesResults) {
        const square = s.taxon?.default_photo?.square_url
        const medium = s.taxon?.default_photo?.medium_url
        if (square) imageUrls.add(square)
        if (medium) imageUrls.add(medium)
    }

    const alreadyCached = new Set(
        (await imageCache.keys()).map(req => req.url)
    )
    const toFetch = [...imageUrls].filter(u => !alreadyCached.has(u))

    total += toFetch.length
    onProgress && onProgress({ done, total })

    const imageTasks = toFetch.map(url => () => fetchAndCache(imageCache, url, 'no-cors'))
    await runBatched(imageTasks, 8, () => {
        done += 1
        onProgress && onProgress({ done, total })
    })

    return { cachedRequests: apiUrls.length, cachedImages: imageUrls.size }
}

export default syncOffline
